// Pure helpers for the multi-row tax encoding grids (VAT output/input today,
// the WHT EWT grid next — see docs/WHT_IMPLEMENTATION.md, WHT-8). No React.
//
// Rows are identified by their `id`: the client row id. It is generated here,
// lives only in the browser (grid state + the local draft), and is never part
// of a save payload. The draft layer (utils/encodingDraft.js) already keys
// confirmed rows on it, so it is also how a save tells the grid and the draft
// which rows to forget.
//
// The contract a save follows:
//   1. classifyRows — send only `toSave`; never send `blank`; keep `incomplete`.
//   2. after the server confirms some rows, rowsAfterSave removes exactly those
//      and tops the grid back up. Nothing else is cleared.

let sequence = 0;

/** A fresh client row id. Unique within the page; not meant to be persisted server-side. */
export const newClientRowId = () => `row-${Date.now().toString(36)}-${(sequence++).toString(36)}`;

const hasId = (row) => row?.id !== undefined && row?.id !== null && row?.id !== '';

/**
 * Ensures every row has a unique id. Rows from older drafts keep the id they
 * have; a row with none — or one that repeats an earlier row's id — gets a new
 * one, since two rows sharing an id would be saved/cleared together.
 */
export function withClientRowIds(rows) {
  const seen = new Set();
  return (rows || []).map((row) => {
    if (hasId(row) && !seen.has(String(row.id))) {
      seen.add(String(row.id));
      return row;
    }
    const id = newClientRowId();
    seen.add(id);
    return { ...row, id };
  });
}

/**
 * Splits the grid for a save. Order is preserved within each bucket.
 * A blank row is never "incomplete", whatever isRowIncomplete says — untouched
 * rows are not the user's mistake and must not be flagged or sent.
 *
 * @returns {{ toSave: Array, incomplete: Array, blank: Array }}
 */
export function classifyRows(rows, isRowIncomplete, isRowBlank) {
  const toSave = [];
  const incomplete = [];
  const blank = [];
  for (const row of rows || []) {
    if (isRowBlank(row)) blank.push(row);
    else if (isRowIncomplete(row)) incomplete.push(row);
    else toSave.push(row);
  }
  return { toSave, incomplete, blank };
}

/**
 * The grid after a save: every row except the confirmed ones, in their
 * original order, topped up with fresh blank rows to at least `minRows`.
 * Ids are compared as strings — drafts hold numbers and strings alike.
 *
 * @param {Array}    rows               current grid rows
 * @param {Iterable} savedClientRowIds  ids of rows the server confirmed
 * @param {number}   minRows            minimum grid length
 * @param {Function} buildEmptyRow      () => blank row (its id, if any, is replaced)
 */
export function rowsAfterSave(rows, savedClientRowIds, minRows, buildEmptyRow) {
  const saved = new Set([...(savedClientRowIds || [])].map(String));
  const remaining = (rows || []).filter((row) => !saved.has(String(row?.id)));
  const missing = Math.max(0, minRows - remaining.length);
  const topUp = Array.from({ length: missing }, () => ({ ...buildEmptyRow(), id: newClientRowId() }));
  return [...remaining, ...topUp];
}

/**
 * For a server that echoes saved records without the client row id: match the
 * echo back to what was sent by a field key. Counted per key, so two identical
 * lines are confirmed only as many times as they were echoed — a short echo
 * never confirms more rows than the server actually returned.
 *
 * @param {Array<{id, record}>} sent   rows in the order sent, with their payload record
 * @param {Array}               echoed records the server returned
 * @param {Function}            keyOf  (record) => string, applied to both sides
 * @returns {Array} ids of the confirmed rows, in sent order
 */
export function confirmedRowIdsFromEcho(sent, echoed, keyOf) {
  const remaining = new Map();
  for (const doc of echoed || []) {
    const key = keyOf(doc);
    remaining.set(key, (remaining.get(key) || 0) + 1);
  }
  const confirmed = [];
  for (const { id, record } of sent || []) {
    const key = keyOf(record);
    const count = remaining.get(key) || 0;
    if (count > 0) {
      remaining.set(key, count - 1);
      confirmed.push(id);
    }
  }
  return confirmed;
}

/**
 * Places per-line server errors on rows. `line` is 1-based in the order rows
 * were sent — the shape routes/percentageTax.js returns
 * (`errors: [{ line, fields }]`). Lines that can't be placed are ignored; the
 * batch-level message still reaches the user through the outcome panel.
 *
 * @param {Array}  sentRowIds  ids in the order their records were sent
 * @param {Array}  lineErrors  [{ line, message? , fields? }]
 * @returns {Object} { [rowId]: message }
 */
export function rowErrorsFromLineErrors(sentRowIds, lineErrors) {
  const byRow = {};
  for (const error of lineErrors || []) {
    const id = sentRowIds?.[(error?.line ?? 0) - 1];
    if (id === undefined) continue;
    byRow[id] = error.message || Object.values(error.fields || {}).join('; ') || 'This line was rejected.';
  }
  return byRow;
}
